import { useDispatch } from "react-redux";

import { CloseIcon, TableViewIcon } from "@/assets/icons";
import { setMarkerInfo } from "@/store/slice/uiSlice";
import { PlaceItem, ReverseGeocodingResponse } from "@/types/place";

type LocationInfoPopupProps = {
  data: PlaceItem | ReverseGeocodingResponse["properties"] | string;
};

const renderItem = (label: string, value: string) => (
  <div className="flex flex-col gap-1">
    <span className="text-xs">{label}</span>
    <span
      className="text-txtHeading text-sm line-clamp-2 text-ellipsis"
      title={value}
    >
      {value}
    </span>
  </div>
);

export const LocationInfoPopup = ({ data }: LocationInfoPopupProps) => {
  const dispatch = useDispatch();

  const handleClose = () => {
    dispatch(setMarkerInfo(null));
  };

  if (typeof data === "string") {
    return (
      <div className="flex flex-col absolute top-[24px] right-[5rem] bg-white rounded-xl p-4 pr-0 z-10 max-w-[340px] w-full gap-5">
        <div className="flex flex-row items-start justify-between h-[40px] mr-4">
          <p className="text-txtHeading font-semibold text-xl truncate">
            No data
          </p>
          <button
            className="h-6 px-1 fill-dim-gray ml-auto"
            data-testid="btn-close"
            onClick={handleClose}
          >
            <CloseIcon />
          </button>
        </div>

        <div className="flex flex-col items-center">
          <TableViewIcon className="w-8 fill-light-gray" />
          <p className="text-xs text-txtPlaceholder">
            There is no data information here.
          </p>
        </div>
      </div>
    );
  }

  function hasNamePreferred(
    data: PlaceItem | ReverseGeocodingResponse["properties"]
  ): data is ReverseGeocodingResponse["properties"] {
    return "name_preferred" in data && typeof data.name_preferred === "string";
  }

  return (
    <div className="flex flex-col absolute top-[24px] right-[5rem] bg-white rounded-md p-4 pr-0 z-10 max-w-[340px] w-full max-h-[90vh] overflow-hidden">
      <div className="flex flex-row items-start justify-between h-[40px] mr-4">
        <p
          className="text-txtHeading font-semibold text-xl truncate"
          title={data.name}
        >
          {data.name}
        </p>
        <button
          className="h-6 px-1 fill-dim-gray ml-auto"
          data-testid="btn-close"
          onClick={handleClose}
        >
          <CloseIcon />
        </button>
      </div>

      <div className="flex flex-col gap-3 max-h-[90vh-100px] overflow-y-auto custom-scrollbar my-1 mr-2">
        {hasNamePreferred(data) &&
          renderItem("Name preferred", data.name_preferred)}
        {renderItem("Latitude", String(data.coordinates.latitude))}
        {renderItem("Longtitude", String(data.coordinates.longitude))}
        {renderItem("Full address", data.full_address)}
        {!hasNamePreferred(data) && renderItem("Type", data.type)}
      </div>
    </div>
  );
};
